import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import baseUrl from '../Utils/baseUrl';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';

// The rest of the app uses axios, which has a global interceptor in
// AuthContext.tsx that adds X-User-Id on every request so the backend IP
// firewall can identify the user. ChatContext uses native fetch (for SSE
// streaming), so it must inject the header itself or prod returns 403.
const authHeaders = (extra?: Record<string, string>): Record<string, string> => {
  const userId = localStorage.getItem('intellasure_user_id');
  const headers: Record<string, string> = { ...(extra || {}) };
  if (userId) headers['X-User-Id'] = userId;
  return headers;
};

interface ChatSession {
  session_id: string;
  title: string;
  created_at: string | null;
  updated_at: string | null;
}

interface Message {
  message: string;
  sender: 'user' | 'ai' | 'loading';
}

interface ChatContextType {
  chatMessages: Message[];
  isLoading: boolean;
  currentStatus: string;
  currentSessionId: string | null;
  sessions: ChatSession[];
  handleSubmitMessage: (message: string) => void;
  clearChat: () => void;
  createNewChat: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType>({
  chatMessages: [],
  isLoading: false,
  currentStatus: '',
  currentSessionId: null,
  sessions: [],
  handleSubmitMessage: () => { console.log("Default handleSubmitMessage called") },
  clearChat: () => { },
  createNewChat: async () => { },
  loadSession: async () => { },
  deleteSession: async () => { },
  refreshSessions: async () => { }
})

export function useChat() {
  return useContext(ChatContext)
}

interface AppProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const { currentProfile } = useAuth();
  const { currentTimeZone } = useData();

  // Load sessions on mount and when user changes
  useEffect(() => {
    if (currentProfile?.userid) {
      refreshSessions();
    }
  }, [currentProfile?.userid]);

  const refreshSessions = async () => {
    if (!currentProfile?.userid) return;

    try {
      const response = await fetch(`${baseUrl}/intellachat/sessions?user_id=${currentProfile.userid}`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const createNewChat = async () => {
    if (!currentProfile?.userid) return;

    try {
      const response = await fetch(`${baseUrl}/intellachat/sessions`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          user_id: currentProfile.userid,
          title: 'New Chat'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentSessionId(data.session_id);
        setChatMessages([]);
        setCurrentStatus('');
        await refreshSessions();
      }
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${baseUrl}/intellachat/sessions/${sessionId}`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSessionId(sessionId);

        // Convert DB messages to chat messages
        const messages: Message[] = data.messages.map((msg: any) => ({
          message: msg.content,
          sender: msg.role === 'user' ? 'user' : 'ai'
        }));
        setChatMessages(messages);
        setCurrentStatus('');
      }
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${baseUrl}/intellachat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (response.ok) {
        // If deleting current session, clear it
        if (sessionId === currentSessionId) {
          setCurrentSessionId(null);
          setChatMessages([]);
        }
        await refreshSessions();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const saveMessage = async (sessionId: string, role: string, content: string) => {
    try {
      await fetch(`${baseUrl}/intellachat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ role, content })
      });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setCurrentStatus('');
    setCurrentSessionId(null);
  };

  const handleSubmitMessage = async (message: string) => {
    setIsLoading(true);
    setCurrentStatus('Connecting...');

    // Create a new session if we don't have one
    let sessionId = currentSessionId;
    if (!sessionId && currentProfile?.userid) {
      try {
        const response = await fetch(`${baseUrl}/intellachat/sessions`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            user_id: currentProfile.userid,
            title: 'New Chat'
          })
        });
        if (response.ok) {
          const data = await response.json();
          sessionId = data.session_id;
          setCurrentSessionId(sessionId);
        }
      } catch (error) {
        console.error('Error creating session:', error);
      }
    }

    const newUserMessage: Message = { message, sender: 'user' };
    const loadingMessage: Message = { message: 'Thinking...', sender: 'loading' };

    setChatMessages(prev => [...prev, newUserMessage, loadingMessage]);

    // Save user message to DB
    if (sessionId) {
      saveMessage(sessionId, 'user', message);
    }

    // Build conversation history for context
    const history = chatMessages
      .filter(msg => msg.sender !== 'loading')
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.message
      }));

    try {
      const response = await fetch(`${baseUrl}/intellachat/stream`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          message: message,
          history: history,
          privilege: currentProfile?.privileges || 'staff',
          department: currentProfile?.department || 'intake',
          tz: currentTimeZone
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let buffer = '';
      let finalResponse = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete message in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'status') {
                // Update the loading message with current status
                setCurrentStatus(data.message);
                setChatMessages(prev => {
                  const updated = [...prev];
                  const loadingIndex = updated.findIndex(m => m.sender === 'loading');
                  if (loadingIndex !== -1) {
                    updated[loadingIndex] = { message: data.message, sender: 'loading' };
                  }
                  return updated;
                });
              } else if (data.type === 'done') {
                finalResponse = data.response;
                // Replace loading message with final response
                setIsLoading(false);
                setCurrentStatus('');
                setChatMessages(prev => {
                  const updated = prev.filter(m => m.sender !== 'loading');
                  if (data.response) {
                    updated.push({ message: data.response, sender: 'ai' });
                  }
                  return updated;
                });

                // Save AI response to DB
                if (sessionId && data.response) {
                  saveMessage(sessionId, 'assistant', data.response);
                  refreshSessions(); // Refresh to update title
                }
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'done') {
            setIsLoading(false);
            setCurrentStatus('');
            setChatMessages(prev => {
              const updated = prev.filter(m => m.sender !== 'loading');
              if (data.response) {
                updated.push({ message: data.response, sender: 'ai' });
              }
              return updated;
            });

            // Save AI response to DB
            if (sessionId && data.response) {
              saveMessage(sessionId, 'assistant', data.response);
              refreshSessions();
            }
          }
        } catch {
          // Ignore parse errors on remaining buffer
        }
      }

    } catch (error) {
      console.error("Error from Intellachat:", error);
      setIsLoading(false);
      setCurrentStatus('');
      setChatMessages(prev => {
        const updated = prev.filter(m => m.sender !== 'loading');
        updated.push({
          message: 'Sorry, I encountered an error. Please try again.',
          sender: 'ai'
        });
        return updated;
      });
    }
  }

  const contextValue: ChatContextType = {
    chatMessages,
    isLoading,
    currentStatus,
    currentSessionId,
    sessions,
    handleSubmitMessage,
    clearChat,
    createNewChat,
    loadSession,
    deleteSession,
    refreshSessions
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  )
}
